import { createSlice } from '@reduxjs/toolkit';

export interface mlInsightsState {
  data: {
    insights: Array<{
      customer: string;
      datetime: string;
      device: string;
      diff: string;
      health_category: string;
      health_score: number;
      insight: string;
      task: string;
      status?: string;
      timestamp: string;
      resolution?: string;
      notificationId?: string;
    }>;
    airFilterInsights: Array<{
      date: string;
      rul_hours: number;
      filter: string;
      filter_health: string;
      date_partition: string;
      device: string;
      cust_code: string;
    }>;
    summary: {
      num_of_insights: number;
      num_of_devices: number;
      num_of_issues: number;
    };
  };
  isMlInsightsLoading: boolean;
  isEvidenceLoading: boolean;
  isUpserting: boolean;
  dateRange: {
    selectedRange: string;
    startDate: string;
    endDate: string;
  };
}

const initialState: mlInsightsState = {
  data: {
    insights: [
      {
        customer: '',
        datetime: '',
        device: '',
        diff: '',
        health_category: '',
        health_score: 0,
        insight: '',
        task: '',
        status: '',
        timestamp: '',
      },
    ],
    airFilterInsights: [],
    summary: {
      num_of_insights: 0,
      num_of_devices: 0,
      num_of_issues: 0,
    },
  },
  isMlInsightsLoading: false,
  isEvidenceLoading: false,
  isUpserting: false,
  dateRange: {
    selectedRange: '14D',
    startDate: '',
    endDate: '',
  },
};

export const mlInsightsSlice = createSlice({
  name: 'mlInsights',
  initialState,
  reducers: {
    setMlInsights(state, { payload }) {
      state.data = { ...state.data, ...payload, insights: [] };
    },
    setMlInsightsLoading(state, { payload }) {
      state.isMlInsightsLoading = payload;
    },
    setEvidenceLoading(state, { payload }) {
      state.isEvidenceLoading = payload;
    },
    setUpserting(state, { payload }) {
      state.isUpserting = payload;
    },
    updateInsightStatus(state, { payload }) {
      const { insightName, deviceId, status, insightDate } = payload;
      const insight = state.data.insights.find(
        (item) =>
          item.insight === insightName &&
          item.device === deviceId &&
          item.timestamp === insightDate
      );
      if (insight) {
        const val = status === 'active' ? 'Active' : 'Inactive';
        insight.status = val;
        insight.resolution = val;
      }
    },
    updateBulkInsightStatus(
      state,
      {
        payload,
      }: {
        payload: Array<{
          insightName: string;
          deviceId: string;
          status: string;
          insightDate: string;
        }>;
      }
    ) {
      payload.forEach(({ insightName, deviceId, status, insightDate }) => {
        const insight = state.data.insights.find(
          (item) =>
            item.insight === insightName &&
            item.device === deviceId &&
            item.timestamp === insightDate
        );
        if (insight) {
          const val = status === 'active' ? 'Active' : 'Inactive';
          insight.status = val;
          insight.resolution = val;
        }
      });
    },
    updateInsightActiveByNotificationId(state, { payload }) {
      const { notificationId, isActive } = payload;
      const insight = state.data.insights.find(
        (item) => item.notificationId === notificationId
      );

      if (insight) {
        const value = isActive ? 'Active' : 'Inactive';
        insight.status = value;
        insight.resolution = value;
      }
    },
    setDateRange(state, { payload }) {
      state.dateRange = payload;
    },
    addAirFilterInsights(state, { payload }) {
      state.data.airFilterInsights = payload;
    },
    appendInsights(state, { payload }) {
      state.data.insights = [...state.data.insights, ...payload];
    },
    clearInsights(state) {
      state.data.insights = [];
      state.data.airFilterInsights = [];
      state.data.summary = {
        num_of_insights: 0,
        num_of_devices: 0,
        num_of_issues: 0,
      };
    },
  },
});

export const {
  setMlInsights,
  setMlInsightsLoading,
  setEvidenceLoading,
  setUpserting,
  updateInsightStatus,
  updateBulkInsightStatus,
  updateInsightActiveByNotificationId,
  setDateRange,
  addAirFilterInsights,
  appendInsights,
  clearInsights,
} = mlInsightsSlice.actions;
export default mlInsightsSlice.reducer;
